import { jwtDecode } from 'jwt-decode';
import { type User } from 'services/user/types';

import { authSessionStore } from './store';
import { type AuthSession, type IAuth } from './types';
import { applyUserSessionSideEffects } from './userEffects';

export type { AuthSession, IAuth } from './types';

class AuthSessionService {
  private loggingOut = false;

  public getSession(): AuthSession {
    return authSessionStore.getSnapshot();
  }

  public beginLogout(): void {
    this.loggingOut = true;
  }

  public isLoggingOut(): boolean {
    return this.loggingOut;
  }

  public subscribe(listener: () => void): () => void {
    return authSessionStore.subscribe(listener);
  }

  public getSnapshot(): AuthSession {
    return authSessionStore.getSnapshot();
  }

  public decode<T>(token: string): T | null {
    try {
      return jwtDecode<T>(token);
    } catch {
      return null;
    }
  }

  public syncUserFromToken(accessToken: string | undefined): User | null {
    if (!accessToken) {
      authSessionStore.setState({ user: null });
      return null;
    }

    const decoded = this.decode<User>(accessToken);

    if (!decoded) {
      authSessionStore.setState({ user: null });
      return null;
    }

    const user = this.normalizeUser(decoded);

    applyUserSessionSideEffects(user);
    authSessionStore.setState({ user });

    return user;
  }

  public setTokens(tokens: IAuth | null): IAuth | null {
    if (!tokens) {
      this.clearSession();
      return null;
    }

    if (this.loggingOut) {
      return null;
    }

    window.sessionStorage.setItem('refreshToken', tokens.refresh);
    authSessionStore.setState({ tokens });
    this.syncUserFromToken(tokens.token);

    return tokens;
  }

  public setUser(user: User | null): User | null {
    if (!user) {
      authSessionStore.setState({ user: null });
      return null;
    }

    const normalizedUser = this.normalizeUser(user);

    applyUserSessionSideEffects(normalizedUser);
    authSessionStore.setState({ user: normalizedUser });

    return normalizedUser;
  }

  public clearSession(): void {
    try {
      window.sessionStorage.removeItem('refreshToken');
    } catch {}

    authSessionStore.reset();
  }

  public getAccessToken(): string | undefined {
    return authSessionStore.getSnapshot().tokens?.token;
  }

  private normalizeUser(user: User): User {
    return {
      ...user,
      acpo: Boolean(user.acpo_original_user_email && user.acpo_original_user_email !== user.user_email)
    };
  }
}

const authSessionService = new AuthSessionService();
export default authSessionService;
